// src/services/payGroups.service.js
// Pay Groups Service - Manage standardized salary structures by job level/grade

import { api } from './api';

/**
 * Get all pay groups with optional filters
 * @param {Object} filters - { is_active: boolean, level: string }
 * @returns {Promise} - Array of pay groups
 */
const getAllPayGroups = async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.is_active !== undefined) {
        params.append('is_active', filters.is_active);
    }
    if (filters.level) {
        params.append('level', filters.level);
    }

    const queryString = params.toString();
    const url = `/api/pay-groups${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data;
};

/**
 * Get a specific pay group by ID with employees
 * @param {number} payGroupId - Pay group ID
 * @returns {Promise} - Pay group object with employees array
 */
const getPayGroupById = async (payGroupId) => {
    const response = await api.get(`/api/pay-groups/${payGroupId}`);
    return response.data;
};

/**
 * Create a new pay group
 * @param {Object} payGroupData - { name, level, description, min_ctc, max_ctc, currency, salary_components }
 * @returns {Promise} - Created pay group object
 */
const createPayGroup = async (payGroupData) => {
    const response = await api.post('/api/pay-groups', payGroupData);
    return response.data;
};

/**
 * Update an existing pay group
 * @param {number} payGroupId - Pay group ID
 * @param {Object} payGroupData - Fields to update
 * @returns {Promise} - Updated pay group object
 */
const updatePayGroup = async (payGroupId, payGroupData) => {
    const response = await api.put(`/api/pay-groups/${payGroupId}`, payGroupData);
    return response.data;
};

/**
 * Delete a pay group (only if no employees assigned)
 * @param {number} payGroupId - Pay group ID
 * @returns {Promise} - Success message
 */
const deletePayGroup = async (payGroupId) => {
    const response = await api.delete(`/api/pay-groups/${payGroupId}`);
    return response.data;
};

/**
 * Get all employees in a pay group
 * @param {number} payGroupId - Pay group ID
 * @returns {Promise} - Array of employees
 */
const getPayGroupEmployees = async (payGroupId) => {
    const response = await api.get(`/api/pay-groups/${payGroupId}/employees`);
    return response.data;
};

/**
 * Bulk assign employees to a pay group
 * @param {number} payGroupId - Pay group ID
 * @param {Array} employeeIds - Array of employee IDs to assign
 * @param {boolean} applySalaryStructure - Whether to apply salary structure
 * @returns {Promise} - { assigned_employees, count }
 */
const assignEmployees = async (payGroupId, employeeIds, applySalaryStructure = false) => {
    const response = await api.post(`/api/pay-groups/${payGroupId}/assign-employees`, {
        employee_ids: employeeIds,
        apply_salary_structure: applySalaryStructure
    });
    return response.data;
};

/**
 * Remove employee from pay group
 * @param {number} payGroupId - Pay group ID
 * @param {number} employeeId - Employee ID
 * @returns {Promise} - Success message
 */
const unassignEmployee = async (payGroupId, employeeId) => {
    const response = await api.delete(`/api/pay-groups/${payGroupId}/employees/${employeeId}`);
    return response.data;
};

/**
 * Get employees without a pay group
 * @returns {Promise} - Array of unassigned employees
 */
const getUnassignedEmployees = async () => {
    const response = await api.get('/api/pay-groups/unassigned/employees');
    return response.data;
};

/**
 * Get pay group statistics
 * @returns {Promise} - { total_pay_groups, active_pay_groups, total_employees_assigned, unassigned_employees }
 */
const getPayGroupStats = async () => {
    const response = await api.get('/api/pay-groups/stats');
    return response.data;
};

/**
 * Get pay group for a specific employee
 * @param {number} employeeId - Employee ID
 * @returns {Promise} - Pay group object or null
 */
const getEmployeePayGroup = async (employeeId) => {
    const response = await api.get(`/api/pay-groups/employee/${employeeId}`);
    return response.data;
};

/**
 * Calculate salary components from pay group template
 * @param {Object} payGroup - Pay group object with salary_components
 * @param {number} ctc - Annual CTC amount
 * @returns {Array} - Array of salary components with calculated amounts
 */
const calculateComponentsFromPayGroup = (payGroup, ctc) => {
    if (!payGroup?.salary_components || payGroup.salary_components.length === 0) {
        return [];
    }

    return payGroup.salary_components.map(comp => ({
        name: comp.name,
        amount: Math.round((ctc * parseFloat(comp.percentage)) / 100),
        monthly_amount: Math.round((ctc * parseFloat(comp.percentage)) / 100 / 12),
        percentage: parseFloat(comp.percentage),
        type: comp.type,
        category: comp.category,
        is_custom: false
    }));
};

/**
 * Validate CTC is within pay group range
 * @param {Object} payGroup - Pay group object
 * @param {number} ctc - Annual CTC to validate
 * @returns {Object} - { isValid: boolean, message: string }
 */
const validateCTCInRange = (payGroup, ctc) => {
    if (!payGroup) {
        return { isValid: true, message: '' };
    }

    const ctcValue = parseFloat(ctc);
    const minCTC = parseFloat(payGroup.min_ctc);
    const maxCTC = parseFloat(payGroup.max_ctc);

    if (ctcValue < minCTC) {
        return {
            isValid: false,
            message: `CTC is below the minimum for ${payGroup.name} (Min: ${minCTC.toLocaleString()})`
        };
    }

    if (ctcValue > maxCTC) {
        return {
            isValid: false,
            message: `CTC exceeds the maximum for ${payGroup.name} (Max: ${maxCTC.toLocaleString()})`
        };
    }

    return { isValid: true, message: '' };
};

/**
 * Auto-assign employees to pay groups based on salary ranges
 * @param {Object} options - { employee_ids: array, mode: 'all'|'unassigned'|'specific', apply_salary_structure: boolean }
 * @returns {Promise} - { summary: {total, assigned, flagged, skipped}, details: [...] }
 */
const autoAssignPayGroups = async (options = {}) => {
    const response = await api.post('/api/pay-groups/auto-assign', options);
    return response.data;
};

/**
 * Get employees flagged for manual assignment
 * @returns {Promise} - { flagged_employees: [...], summary: {...} }
 */
const getFlaggedEmployees = async () => {
    const response = await api.get('/api/pay-groups/flagged-employees');
    return response.data;
};

/**
 * Resolve a flagged employee by manually assigning to a pay group
 * @param {string} employeeId - Employee ID
 * @param {Object} data - { pay_group_id: number, apply_salary_structure: boolean }
 * @returns {Promise} - Assignment result
 */
const resolveAssignmentFlag = async (employeeId, data) => {
    const response = await api.post(`/api/pay-groups/resolve-flag/${employeeId}`, data);
    return response.data;
};

/**
 * Get assignment history/audit log
 * @param {Object} filters - { employee_id, pay_group_id, assignment_type, limit, offset }
 * @returns {Promise} - { assignments: [...], pagination: {...} }
 */
const getAssignmentHistory = async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.employee_id) params.append('employee_id', filters.employee_id);
    if (filters.pay_group_id) params.append('pay_group_id', filters.pay_group_id);
    if (filters.assignment_type) params.append('assignment_type', filters.assignment_type);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const queryString = params.toString();
    const url = `/api/pay-groups/assignment-history${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data;
};

/**
 * Preview what pay group an employee would be assigned to
 * @param {string} employeeId - Employee ID
 * @returns {Promise} - { preview: {would_assign, pay_group_id, flag_reason, message} }
 */
const getAssignmentPreview = async (employeeId) => {
    const response = await api.get(`/api/pay-groups/assignment-preview/${employeeId}`);
    return response.data;
};

/**
 * Sync single employee's salary components with their pay group template
 * @param {string} employeeId - Employee ID
 * @returns {Promise} - { success, message, employee_name, components, ctc }
 */
const syncEmployeeWithPayGroup = async (employeeId) => {
    const response = await api.post(`/api/pay-groups/sync-employee/${employeeId}`);
    return response;
};

/**
 * Cascade pay group template changes to all employees in the group
 * @param {number} payGroupId - Pay group ID
 * @returns {Promise} - { success, message, updated_count, total_count, updated_employees, errors }
 */
const cascadePayGroupUpdate = async (payGroupId) => {
    const response = await api.post(`/api/pay-groups/${payGroupId}/cascade-update`);
    return response;
};

/**
 * Unassign all employees company-wide from their pay groups
 * @param {Object} options - { confirmation: boolean, clear_salary_data: boolean }
 * @returns {Promise} - { success, message, unassigned_count, salary_data_cleared, employees }
 */
const unassignAllCompanyWide = async (options = {}) => {
    const response = await api.post('/api/pay-groups/unassign-all-company', options);
    return response.data;
};

/**
 * Unassign all employees from a specific pay group
 * @param {number} payGroupId - Pay group ID
 * @param {Object} options - { clear_salary_data: boolean }
 * @returns {Promise} - { success, message, unassigned_count, pay_group_name, salary_data_cleared, employees }
 */
const unassignAllFromPayGroup = async (payGroupId, options = {}) => {
    const response = await api.post(`/api/pay-groups/${payGroupId}/unassign-all`, options);
    return response.data;
};

const payGroupsService = {
    getAllPayGroups,
    getPayGroupById,
    createPayGroup,
    updatePayGroup,
    deletePayGroup,
    getPayGroupEmployees,
    assignEmployees,
    unassignEmployee,
    getUnassignedEmployees,
    getPayGroupStats,
    getEmployeePayGroup,
    calculateComponentsFromPayGroup,
    validateCTCInRange,
    // Auto-assignment methods
    autoAssignPayGroups,
    getFlaggedEmployees,
    resolveAssignmentFlag,
    getAssignmentHistory,
    getAssignmentPreview,
    // Sync methods
    syncEmployeeWithPayGroup,
    cascadePayGroupUpdate,
    // Unassign methods
    unassignAllCompanyWide,
    unassignAllFromPayGroup,
};

export default payGroupsService;
